import React from "react";
import { InsightCards, KpiTile, Stat } from "./StatsTiles";
import { DEFINITIONS } from "./beautyStatsDefinitions";
import { computeDelta, fmtHours, fmtInt, fmtMoney, fmtMoneyCompact, fmtNumber1, fmtPct } from "./statsFormat";
import { sparkValues } from "./statsChart";
import { buildSeries, deriveMetrics, selectInsights } from "./beautyStatsMetrics";

/**
 * Resumen tab of the Beauty statistics: four headline tiles (with sparkline and change vs the
 * comparison period), the rates strip (cancellations, no-shows, free agenda hours, share of the
 * appointments that came through a professional's link) and up to three insights.
 *
 * A linked professional sees their own numbers (the back scopes the response): the fourth tile is
 * then "Tu comisión" instead of "Ticket promedio", and the occupancy tile says "Tu".
 *
 * @param {{current: object, comparison: object|null, compareLabel: string, bucket: number, linked: boolean}} props
 *   `current` / `comparison` are stats responses; `bucket` is the sparkline grouping (1 or 7 days).
 */
export default function BeautyResumenTab({ current, comparison, compareLabel, bucket, linked }) {
  const m = deriveMetrics(current);
  const c = deriveMetrics(comparison);
  const spark = (metric) => sparkValues(buildSeries(current.daily, metric, bucket));
  const insights = selectInsights(current);
  const unknownAmounts = current.totals.amountUnknownAppointments;

  return (
    <>
      <section className="stats-kpis" aria-label="Indicadores principales">
        <KpiTile
          icon="occ"
          definition={DEFINITIONS.occ}
          label={linked ? "Tu ocupación de agenda" : "Ocupación de agenda"}
          value={fmtPct(m.occupancy)}
          delta={computeDelta("pp", m.occupancy, c?.occupancy, true)}
          compareLabel={compareLabel}
          sub={`${fmtHours(m.bookedHours)} con turno de ${fmtHours(m.agendaHours)}`}
          spark={spark("occ")}
        />
        <KpiTile
          icon="rev"
          definition={DEFINITIONS.rev}
          label="Ingresos cobrados"
          value={fmtMoneyCompact(m.revenue)}
          delta={computeDelta("pct", m.revenue, c?.revenue, true)}
          compareLabel={compareLabel}
          sub={`Pendiente de cobro: ${fmtMoney(m.pending)}`}
          spark={spark("rev")}
        />
        <KpiTile
          icon="res"
          definition={DEFINITIONS.res}
          label="Turnos"
          value={fmtInt(m.appointments)}
          delta={computeDelta("pct", m.appointments, c?.appointments, true)}
          compareLabel={compareLabel}
          sub={`${fmtNumber1(m.perDay)} por día en promedio`}
          spark={spark("res")}
        />
        {linked ? (
          <KpiTile
            icon="comm"
            definition={DEFINITIONS.comm}
            label="Tu comisión"
            value={fmtMoneyCompact(m.commission)}
            delta={computeDelta("pct", m.commission, c?.commission, true)}
            compareLabel={compareLabel}
            sub="Sobre tus turnos atendidos"
            spark={spark("rev")}
          />
        ) : (
          <KpiTile
            icon="ticket"
            definition={DEFINITIONS.ticket}
            label="Ticket promedio"
            value={fmtMoney(m.ticket)}
            delta={computeDelta("pct", m.ticket, c?.ticket, true)}
            compareLabel={compareLabel}
            sub={`${fmtNumber1(m.servicesPerAppointment)} servicios por turno`}
            spark={spark("ticket")}
          />
        )}
      </section>

      <section className="stats-strip" aria-label="Cancelaciones, ausencias, agenda libre y link del profesional">
        <Stat
          definition={DEFINITIONS.canc}
          label="Cancelaciones"
          value={fmtPct(m.cancellationRate)}
          delta={computeDelta("pp", m.cancellationRate, c?.cancellationRate, false)}
          compareLabel={compareLabel}
          sub={`${fmtInt(m.cancelled)} turnos cancelados`}
        />
        <Stat
          definition={DEFINITIONS.ns}
          label="No asistió"
          value={fmtPct(m.noShowRate)}
          delta={computeDelta("pp", m.noShowRate, c?.noShowRate, false)}
          compareLabel={compareLabel}
          sub={`Sin cobrar: ${fmtMoney(m.noShowUncollected)}`}
        />
        <Stat
          definition={DEFINITIONS.free}
          label="Horas libres en agenda"
          value={fmtHours(m.freeHours)}
          delta={computeDelta("pct", m.freeHours, c?.freeHours, false)}
          compareLabel={compareLabel}
          sub={`de ${fmtHours(m.agendaHours)} de agenda`}
        />
        <Stat
          definition={DEFINITIONS.link}
          label="Por el link del profesional"
          value={fmtPct(m.viaLinkShare)}
          delta={computeDelta("pp", m.viaLinkShare, c?.viaLinkShare, true)}
          compareLabel={compareLabel}
          sub={`${fmtInt(m.linkCount)} turnos`}
        />
      </section>

      <InsightCards insights={insights} />

      {unknownAmounts > 0 && (
        <p className="stats-footnote">
          {fmtInt(unknownAmounts)} {unknownAmounts === 1 ? "turno no tiene" : "turnos no tienen"} el total registrado, así que{" "}
          {unknownAmounts === 1 ? "no suma" : "no suman"} a lo pendiente de cobro ni a lo que se perdió por ausencias.
        </p>
      )}
    </>
  );
}
